// dataTransformer.js
/**
 * Утилиты для преобразования данных из различных форматов в единый формат для диаграммы Ганта
 */

/**
 * Основная функция преобразования данных
 * @param {Array} rawData - сырые данные из API
 * @param {string} dataType - тип данных ('orders', 'operations', 'resources')
 * @returns {Array} преобразованные данные в едином формате
 */
export const transformData = (rawData, dataType = 'orders') => {
    if (!rawData || !Array.isArray(rawData)) {
        console.warn('Некорректные данные для преобразования:', rawData);
        return [];
    }

    try {
        switch (dataType) {
            case 'orders':
                return transformOrdersData(rawData);
            case 'operations':
                return transformOperationsData(rawData);
            case 'resources':
                return transformResourcesData(rawData);
            default:
                console.warn(`Неизвестный тип данных: ${dataType}`);
                return rawData;
        }
    } catch (error) {
        console.error('Ошибка при преобразовании данных:', error);
        return [];
    }
};

/**
 * Преобразование данных заказов
 */
const transformOrdersData = (orders) => {
    return orders.map(order => ({
        // Основные поля
        id: getValue(order, ['id', 'orderId', 'agentId', 'ID']),
        orderNumber: getValue(order, ['orderNumber', 'nameOrder', 'number', 'code']),
        name: getValue(order, ['name', 'orderName', 'title', 'description']),
        status: getStatusValue(order),

        // Даты
        startDate: getDateValue(order, 'startDate'),
        endDate: getDateValue(order, 'endDate'),

        // Дополнительные поля
        description: getValue(order, ['description', 'comment', 'notes']),

        // Вложенные операции (если есть)
        operations: transformOperationsData(getValue(order, ['operations', 'tasks', 'children']) || [])
    })).filter(order => order.id); // Фильтруем пустые записи
};

/**
 * Преобразование данных операций
 */
const transformOperationsData = (operations) => {
    return operations.map(operation => ({
        // Основные поля
        id: getValue(operation, ['id', 'operationId', 'taskId', 'ID']),
        operationNumber: getValue(operation, ['operationNumber', 'taskNumber', 'number', 'code']),
        name: getValue(operation, ['name', 'operationName', 'taskName', 'title']),
        status: getStatusValue(operation),

        // Даты
        startDate: getDateValue(operation, 'startDate'),
        endDate: getValue(operation, ['endDate', 'timeEnd', 'finishDate']),

        // Дополнительные поля
        description: getValue(operation, ['description', 'comment', 'notes']),

        // Вложенные ресурсы (если есть)
        resources: transformResourcesData(getValue(operation, ['resources', 'agents', 'workers']) || [])
    })).filter(operation => operation.id);
};

/**
 * Преобразование данных ресурсов
 */
const transformResourcesData = (resources) => {
    return resources.map(resource => ({
        // Основные поля
        id: getValue(resource, ['id', 'agentId', 'resourceId', 'workerId']),
        name: getValue(resource, ['name', 'agentName', 'resourceName', 'workerName']),
        type: getResourceType(resource),
        status: getStatusValue(resource),

        // Даты
        startDate: getDateValue(resource, 'startDate'),
        endDate: getDateValue(resource, 'endDate'),

        // Дополнительные поля
        description: getValue(resource, ['description', 'role', 'position', 'comment'])
    })).filter(resource => resource.id);
};

/**
 * Универсальный getter для получения значения по приоритету ключей
 */
const getValue = (obj, keys) => {
    if (!obj || typeof obj !== 'object') return null;

    for (const key of keys) {
        if (obj[key] !== undefined && obj[key] !== null && obj[key] !== '') {
            return obj[key];
        }
    }
    return null;
};

/**
 * Получение значения статуса с преобразованием в числовой формат
 */
const getStatusValue = (obj) => {
    const status = getValue(obj, ['status', 'state', 'progress']);

    if (typeof status === 'number') return status;
    if (typeof status === 'string') {
        // Преобразование строковых статусов в числовые
        const statusMap = {
            'approved': 1,
            'completed': 2,
            'booked': 3,
            'cancelled': 4,
            'ready': 5,
            'in_progress': 6,
            'pending': 7,
            'утверждено': 1,
            'завершено': 2,
            'забронировано': 3,
            'отменено': 4,
            'готов': 5,
            'в работе': 6,
            'на рассмотрении': 7
        };
        return statusMap[status.toLowerCase()] || 1;
    }
    return 1; // Статус по умолчанию
};

/**
 * Получение и нормализация даты
 */
const getDateValue = (obj, dateType) => {
    const date = getValue(obj, [
        dateType,
        `time${dateType.charAt(0).toUpperCase() + dateType.slice(1)}`, // timeStart, timeEnd
        `${dateType}Time`,
        `${dateType}Date`
    ]);

    if (!date) return null;

    try {
        // Если дата в формате массива [год, месяц, день, час, минута]
        if (Array.isArray(date)) {
            return new Date(
                date[0],
                date[1] - 1, // месяцы в JS 0-11
                date[2] || 1,
                date[3] || 8, // время по умолчанию 8:00
                date[4] || 0
            ).toISOString();
        }

        // Если это строка или timestamp
        const parsedDate = new Date(date);
        return isNaN(parsedDate.getTime()) ? null : parsedDate.toISOString();
    } catch (error) {
        console.warn('Ошибка парсинга даты:', date, error);
        return null;
    }
};

/**
 * Определение типа ресурса
 */
const getResourceType = (resource) => {
    const type = getValue(resource, ['type', 'resourceType', 'agentType']);

    if (type) {
        const typeMap = {
            'human': 'Человек',
            'person': 'Человек',
            'worker': 'Человек',
            'employee': 'Человек',
            'equipment': 'Оборудование',
            'machine': 'Оборудование',
            'tool': 'Оборудование',
            'material': 'Материал'
        };
        return typeMap[type.toLowerCase()] || type;
    }

    // Определение по другим признакам
    if (resource.hasOwnProperty('employeeId') || resource.hasOwnProperty('workerName')) {
        return 'Человек';
    }
    if (resource.hasOwnProperty('machineId') || resource.hasOwnProperty('equipmentCode')) {
        return 'Оборудование';
    }

    return 'Неизвестно';
};

/**
 * Создание иерархической структуры из плоских данных
 */
export const buildHierarchy = (ordersData, operationsData = [], resourcesData = []) => {
    const transformedOrders = transformData(ordersData, 'orders');
    const transformedOperations = transformData(operationsData, 'operations');
    const transformedResources = transformData(resourcesData, 'resources');

    // Связываем операции с заказами
    const ordersWithOperations = transformedOrders.map(order => {
        const orderOperations = transformedOperations.filter(op =>
            op.orderId === order.id ||
            op.orderNumber === order.orderNumber ||
            // Дополнительная логика связывания, если нужно
            op.name?.includes(order.name)
        );

        return {
            ...order,
            operations: orderOperations.map(operation => {
                const operationResources = transformedResources.filter(resource =>
                    resource.operationId === operation.id ||
                    resource.operationNumber === operation.operationNumber
                );

                return {
                    ...operation,
                    resources: operationResources
                };
            })
        };
    });

    return ordersWithOperations;
};

/**
 * Вычисление дат заказа на основе операций
 * @param {Array} orders - массив заказов с вложенными operations
 * @returns {Array} заказы с вычисленными startDate/endDate
 */
export const computeOrderDates = (orders) => {
    return orders.map(order => {
        if (!order.operations || order.operations.length === 0) {
            return order; // Не меняем даты, если их нет
        }

        let minStart = null;
        let maxEnd = null;

        // Рекурсивно собираем все даты из операций и ресурсов
        const collectDates = (items) => {
            items.forEach(item => {
                if (item.startDate) {
                    const start = new Date(item.startDate);
                    if (!minStart || start < minStart) minStart = start;
                }
                if (item.endDate) {
                    const end = new Date(item.endDate);
                    if (!maxEnd || end > maxEnd) maxEnd = end;
                }

                // Рекурсивно для вложенных элементов
                if (item.operations) collectDates(item.operations);
                if (item.resources) collectDates(item.resources);
            });
        };

        collectDates(order.operations);

        // Обновляем даты заказа только если нашли более точные
        if (minStart && maxEnd) {
            return {
                ...order,
                startDate: minStart.toISOString(),
                endDate: maxEnd.toISOString()
            };
        }

        return order;
    });
};

/**
 * Валидация преобразованных данных
 */
export const validateTransformedData = (data) => {
    if (!Array.isArray(data)) return false;

    return data.every(item => {
        const hasRequiredFields = item.id && item.name;
        const hasValidDates = !item.startDate || !isNaN(new Date(item.startDate).getTime());
        const hasValidStatus = typeof item.status === 'number' && item.status >= 1 && item.status <= 10;

        return hasRequiredFields && hasValidDates && hasValidStatus;
    });
};

/**
 * Дополнительные утилиты для работы с данными
 */
export const DataUtils = {
    // Фильтрация по статусу
    filterByStatus: (data, status) => {
        return data.filter(item => item.status === status);
    },

    // Фильтрация по дате
    filterByDateRange: (data, startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);

        return data.filter(item => {
            const itemStart = new Date(item.startDate);
            const itemEnd = new Date(item.endDate);
            return itemStart >= start && itemEnd <= end;
        });
    },

    // Поиск по названию
    searchByName: (data, searchTerm) => {
        const term = searchTerm.toLowerCase();
        return data.filter(item =>
            item.name?.toLowerCase().includes(term) ||
            item.orderNumber?.toLowerCase().includes(term) ||
            item.description?.toLowerCase().includes(term)
        );
    },

    // Получение временного диапазона из данных
    getTimeRange: (data) => {
        if (!data.length) return { start: null, end: null };

        let minDate = null;
        let maxDate = null;

        const extractDates = (item) => {
            if (item.startDate) {
                const start = new Date(item.startDate);
                if (!minDate || start < minDate) minDate = start;
            }
            if (item.endDate) {
                const end = new Date(item.endDate);
                if (!maxDate || end > maxDate) maxDate = end;
            }
        };

        data.forEach(item => {
            extractDates(item);
            item.operations?.forEach(extractDates);
            item.resources?.forEach(extractDates);
        });

        // Добавляем отступы
        if (minDate && maxDate) {
            minDate.setDate(minDate.getDate() - 1);
            maxDate.setDate(maxDate.getDate() + 1);
        }

        return { start: minDate, end: maxDate };
    }
};

export default {
    transformData,
    buildHierarchy,
    computeOrderDates,
    validateTransformedData,
    DataUtils
};